import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, AlertTriangle, MapPin, Building2, User, Wrench, Send, Sparkles, X,
  Link2, Clock, FileText, CheckCircle2, HelpCircle, ShieldAlert, History,
  CheckSquare, MessageCircle, Inbox, Pencil, Mail, Paperclip,
} from "lucide-react";
import { URGENCY, STATUS_LABELS, STATUS_ORDER, catLabel } from "@/pages/pratiche/constants";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-[#007BFF]" />
      <h3 className="text-sm font-bold">{title}</h3>
    </div>
    {children}
  </div>
);

export const PraticaDetail = ({ id, onChange = () => {}, onClose = () => {} }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [reply, setReply] = useState("");
  const [interv, setInterv] = useState("");
  const [supEmail, setSupEmail] = useState("");
  const [supName, setSupName] = useState("");
  const [supId, setSupId] = useState("");
  const [supPhone, setSupPhone] = useState("");
  const [interChannel, setInterChannel] = useState("email");
  const [note, setNote] = useState("");
  const [notePublic, setNotePublic] = useState(false);
  const [condoAtts, setCondoAtts] = useState([]);
  const [interAtts, setInterAtts] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState("");
  const [showClose, setShowClose] = useState(false);
  const [checklist, setChecklist] = useState({ problema_risolto: false, condomino_informato: false, documenti_ricevuti: false });
  const [closeCost, setCloseCost] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [buildings, setBuildings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editCondo, setEditCondo] = useState(false);
  const [edBuilding, setEdBuilding] = useState("");
  const [edPlace, setEdPlace] = useState("");
  const [edSender, setEdSender] = useState("");
  const [edSenderEmail, setEdSenderEmail] = useState("");
  const [edSenderPhone, setEdSenderPhone] = useState("");
  const [edResidentId, setEdResidentId] = useState("");
  const [edResidents, setEdResidents] = useState([]);
  const [savingCondo, setSavingCondo] = useState(false);
  const [replyChannel, setReplyChannel] = useState("email");
  const [condoWa, setCondoWa] = useState("");
  const [waHintDismissed, setWaHintDismissed] = useState(false);
  const [contactInput, setContactInput] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    api.get("/buildings").then((r) => setBuildings(r.data || [])).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!edBuilding) { setEdResidents([]); return; }
    api.get(`/buildings/${edBuilding}/residents`).then((r) => setEdResidents(r.data || [])).catch(() => setEdResidents([]));
  }, [edBuilding]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/pratiche/${id}`);
      setP(r.data);
      setReply((r.data.drafts || {}).condomino_reply || "");
      setInterv((r.data.drafts || {}).intervention_request || "");
      setReplyChannel((r.data.channel === "whatsapp" || r.data.channel === "sms") ? "sms" : "email");
      setCondoWa(r.data.wa_contact || "");
      setEdBuilding((r.data.condo || {}).building_id || "");
      setEdPlace(r.data.place || "");
      setEdSender(r.data.sender_name || "");
      setEdSenderEmail(r.data.sender_email || "");
      setEdSenderPhone(r.data.wa_contact || "");
      setEdResidentId((r.data.condo || {}).resident_id || "");
      const best = (r.data.supplier_suggestion || {}).best;
      if (best) { setSupEmail(best.email || ""); setSupName(best.name || ""); setSupId(best.id || ""); setSupPhone(best.phone || ""); }
    } catch { toast.error("Impossibile caricare la pratica"); } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try { await api.put(`/pratiche/${id}/status`, { status }); toast.success("Stato aggiornato"); load(); onChange(); }
    catch { toast.error("Errore aggiornamento stato"); }
  };

  const approve = async (action, extra = {}) => {
    setBusy(action);
    try {
      await api.post(`/pratiche/${id}/approve`, { action, ...extra });
      toast.success("Azione approvata ed eseguita");
      load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Azione non riuscita"); }
    finally { setBusy(""); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/pratiche/${id}/note`, { note, public: notePublic });
      setNote("");
      toast.success(notePublic ? "Aggiornamento inviato al condòmino" : "Nota aggiunta");
      setNotePublic(false);
      load();
    }
    catch { toast.error("Errore"); }
  };

  const linkPratica = async (otherId) => {
    try { await api.post(`/pratiche/${id}/link`, { other_id: otherId }); toast.success("Pratiche collegate"); load(); onChange(); }
    catch { toast.error("Errore collegamento"); }
  };

  const uploadAttachment = async (e, which) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAtt(which);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/pratiche/${id}/reply-attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const isImage = /\.(png|jpe?g|webp|gif)$/i.test(f.name) || (f.type || "").startsWith("image/");
      const preview = isImage ? URL.createObjectURL(f) : "";
      const item = { file_id: r.data.file_id, filename: r.data.filename, isImage, preview };
      if (which === "condo") setCondoAtts((a) => [...a, item]);
      else setInterAtts((a) => [...a, item]);
      toast.success("Allegato aggiunto");
    } catch { toast.error("Errore caricamento allegato"); }
    finally { setUploadingAtt(""); e.target.value = ""; }
  };

  const openAttachment = async (fid) => {
    try {
      const r = await api.get(`/attachments/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Impossibile aprire l'allegato"); }
  };

  const removeAtt = (which, fid) => {
    if (which === "condo") setCondoAtts((a) => a.filter((x) => x.file_id !== fid));
    else setInterAtts((a) => a.filter((x) => x.file_id !== fid));
  };

  const markCondominoMsgSeen = async () => {
    try { await api.post(`/pratiche/${id}/condomino-msg-seen`); toast.success("Richieste segnate come lette"); load(); onChange(); }
    catch { toast.error("Errore"); }
  };

  const doClose = async () => {
    try {
      await api.post(`/pratiche/${id}/close`, { checklist, cost_actual: closeCost ? parseFloat(closeCost) : null, note: closeNotes });
      toast.success("Pratica chiusa"); setShowClose(false); setCloseNotes(""); load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore in chiusura"); }
  };

  const doReopen = async () => {
    setBusy("reopen");
    try {
      await api.post(`/pratiche/${id}/reopen`);
      toast.success("Pratica riaperta"); load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella riapertura"); }
    finally { setBusy(""); }
  };

  const pickResident = (rid) => {
    if (rid === "__manual__") { setEdResidentId(""); return; }
    setEdResidentId(rid);
    const res = edResidents.find((x) => x.id === rid);
    if (res) {
      setEdSender(res.nome || "");
      setEdSenderEmail(res.email || "");
      setEdSenderPhone(res.telefono || "");
    }
  };

  const saveCondo = async () => {
    setSavingCondo(true);
    try {
      await api.put(`/pratiche/${id}/condo`, {
        building_id: edBuilding, place: edPlace,
        sender_name: edSender.trim(), sender_email: edSenderEmail.trim(),
        wa_contact: edSenderPhone.trim(), resident_id: edResidentId,
      });
      toast.success("Dati pratica aggiornati");
      setEditCondo(false); load(); onChange();
    } catch { toast.error("Errore aggiornamento pratica"); }
    finally { setSavingCondo(false); }
  };

  const quickSetCondo = async (bid) => {
    if (!bid) return;
    setSavingCondo(true);
    try {
      setEdBuilding(bid);
      await api.put(`/pratiche/${id}/condo`, { building_id: bid });
      const b = buildings.find((x) => x.id === bid);
      toast.success(`Condominio "${b?.name || ""}" collegato alla pratica`);
      load(); onChange();
    } catch { toast.error("Errore nel collegare il condominio"); }
    finally { setSavingCondo(false); }
  };

  const addContactInline = async (mode, ridOrBuilding, name) => {
    const email = (contactInput || "").trim();
    if (!email || !email.includes("@")) { toast.error("Inserisci un'email valida."); return; }
    setSavingContact(true);
    try {
      if (mode === "fill") {
        await api.put(`/residents/${ridOrBuilding}`, { email });
      } else {
        if (!ridOrBuilding) { toast.error("Assegna prima un condominio alla pratica per creare il contatto."); setSavingContact(false); return; }
        await api.post("/residents", { building_id: ridOrBuilding, nome: name || "Condòmino", email });
      }
      toast.success("Contatto salvato in anagrafica");
      setContactInput(""); load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio del contatto"); }
    finally { setSavingContact(false); }
  };

  const pickSupplier = (sid) => {
    if (sid === "__manual__") { setSupId(""); setSupName(""); setSupEmail(""); setSupPhone(""); return; }
    const s = suppliers.find((x) => x.id === sid);
    if (!s) return;
    setSupId(s.id); setSupName(s.name || ""); setSupEmail(s.email || ""); setSupPhone(s.phone || "");
    if (s.phone && !s.email && waEnabled) setInterChannel("sms");
    toast.success(`Fornitore "${s.name}" selezionato`);
  };

  if (loading || !p) {
    return <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const u = URGENCY[(p.urgency || {}).level] || URGENCY.normale;
  const condo = p.condo || {};
  // SMS nelle pratiche: incluso nel piano Elite del condominio.
  // Richiede SEMPRE un condominio indicato: gli SMS vengono scalati dalla quota di QUEL condominio.
  const waPlanOk = !!(p.sms_enabled ?? p.whatsapp_enabled);      // condominio della pratica in piano Elite (o esente)
  const waEnabled = waPlanOk && !!condo.building_id;             // abilitato solo con condominio indicato
  const waSenderSet = p.sms_ready !== false;                     // servizio SMS attivo lato Sedio
  const waDisabledReason = !condo.building_id
    ? "Indica il condominio della pratica per rispondere via SMS: verranno scalati dalla quota di quel condominio"
    : (!waPlanOk ? "L'invio via SMS nelle pratiche è incluso solo nel piano Elite del condominio" : "");
  const linkedResident = condo.resident_id ? edResidents.find((r) => r.id === condo.resident_id) : null;
  const roleBadge = (ruolo) => {
    const rl = (ruolo || "").toLowerCase();
    if (rl === "proprietario") return <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">Proprietario</span>;
    if (rl === "inquilino") return <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">Inquilino</span>;
    return null;
  };
  const sug = p.supplier_suggestion || {};
  const cc = p.condomino_contact || {};
  const contactEmail = (cc.email || "").trim();
  const fallbackEmail = (p.sender_email && p.sender_email.includes("@")) ? p.sender_email : "";
  const emailTo = contactEmail || fallbackEmail;
  const condominoName = cc.name || p.sender_name || "";
  const replyChannels = ["email", "sms"];
  const effReplyChannel = (replyChannels.includes(replyChannel) && (replyChannel !== "sms" || waEnabled)) ? replyChannel : "email";
  const replyTo = effReplyChannel === "sms" ? (condoWa || "").trim() : emailTo;
  const isClosed = ["chiusa", "annullata"].includes(p.status);
  const closedLabel = p.status === "annullata" ? "Annullata" : "Chiusa";

  return (
    <div className="p-5 space-y-4 max-w-3xl mx-auto" data-testid="pratica-detail">
      {/* Torna alla lista (solo mobile) */}
      <button data-testid="pratica-back-mobile" onClick={onClose}
        className="md:hidden -mt-1 mb-1 inline-flex items-center gap-1.5 text-sm font-medium text-[#007BFF]">
        <X className="h-4 w-4" /> Torna alle pratiche
      </button>

      {(p.urgency || {}).emergency && (
        <div className="bg-red-600 text-white rounded-lg p-4 flex items-start gap-3" data-testid="pratica-emergency-banner">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold">Possibile emergenza rilevata</div>
            <div className="text-white/90 mt-0.5">In presenza di un pericolo immediato per persone o cose, contattare i servizi di emergenza competenti. L'AI non sostituisce i soccorsi.</div>
          </div>
        </div>
      )}

      {/* Richieste dal condòmino via portale */}
      {p.condomino_msg_unseen && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4" data-testid="condomino-msg-banner">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h3 className="text-sm font-bold flex items-center gap-2 text-emerald-800"><Inbox className="h-4 w-4" />Nuova richiesta dal condòmino</h3>
            <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-100" data-testid="condomino-msg-seen" onClick={markCondominoMsgSeen}>Segna come letto</Button>
          </div>
          <ul className="space-y-1">
            {(p.history || []).filter((h) => h.type === "condomino_msg").slice(-4).reverse().map((h, i) => (
              <li key={i} className="text-xs text-emerald-900">
                <span className="text-[10px] text-emerald-600">{(h.ts || "").replace("T", " ").slice(0, 16)}</span>
                <div>{(h.label || "").replace("📩 Richiesta dal condòmino via portale: ", "")}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-muted-foreground">{p.ref}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${u.cls}`}>{u.label}</span>
            {isClosed && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-700 text-white inline-flex items-center gap-1" data-testid="pratica-closed-badge">
                <CheckSquare className="h-3 w-3" />{closedLabel}
              </span>
            )}
          </div>
          <h2 className="font-head text-xl font-extrabold tracking-tight mt-1">{p.subject}</h2>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary md:hidden"><X className="h-5 w-5" /></button>
      </div>

      {/* Motivo della segnalazione — evidenziato */}
      {p.problem && (
        <div className="rounded-lg border-l-4 border-amber-400 bg-amber-50 px-4 py-3" data-testid="pratica-motivo">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Motivo della segnalazione
          </div>
          <p className="text-sm text-amber-900 leading-relaxed">{p.problem}</p>
        </div>
      )}

      {/* Allegati inviati dal condòmino con la segnalazione */}
      {(p.attachments || []).length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="pratica-seg-attachments">
          <div className="flex items-center gap-2 mb-3">
            <Paperclip className="h-4 w-4 text-[#007BFF]" />
            <h3 className="text-sm font-bold">Allegati alla segnalazione</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.attachments.map((a) => {
              const isImg = /\.(png|jpe?g|webp|gif)$/i.test(a.filename || "") || (a.content_type || "").startsWith("image/");
              return (
                <button key={a.file_id} type="button" onClick={() => openAttachment(a.file_id)}
                  className="flex items-center gap-2 text-xs bg-secondary rounded-md p-1.5 pr-2 border border-border hover:border-[#007BFF] hover:text-[#007BFF] transition-colors"
                  data-testid={`seg-attachment-${a.file_id}`} title="Apri allegato">
                  <span className="h-9 w-9 rounded bg-[#007BFF]/10 flex items-center justify-center shrink-0">
                    {isImg ? <img src={`${API}/attachments/${a.file_id}/thumb`} alt="" className="h-9 w-9 rounded object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <FileText className="h-4 w-4 text-[#007BFF]" />}
                  </span>
                  <span className="truncate max-w-[160px]">{a.filename}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Azioni pratica */}
      <div className="flex flex-wrap gap-2" data-testid="pratica-actions">
        {isClosed ? (
          <Button size="sm" className="h-8 text-xs bg-[#007BFF] hover:bg-[#0056B3]" data-testid="pratica-reopen-btn" onClick={doReopen} disabled={busy === "reopen"}>
            {busy === "reopen" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <History className="h-3.5 w-3.5 mr-1.5" />}Riapri pratica
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="pratica-close-btn" onClick={() => setShowClose((v) => !v)}>
              <CheckSquare className="h-3.5 w-3.5 mr-1.5" />Chiudi pratica
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="pratica-edit-condo-btn" onClick={() => setEditCondo((v) => !v)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifica condominio/luogo/segnalante
            </Button>
          </>
        )}
      </div>

      {!isClosed && showClose && (
        <div className="bg-card border border-[#007BFF]/40 rounded-lg p-4" data-testid="pratica-close-panel">
          <h3 className="text-sm font-bold mb-1 flex items-center gap-2"><CheckSquare className="h-4 w-4 text-[#007BFF]" />Chiusura pratica</h3>
          <p className="text-xs text-muted-foreground mb-2">Puoi chiudere la pratica liberamente. I flag qui sotto e il costo sono facoltativi.</p>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Note di chiusura</label>
          <textarea value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} rows={3}
            placeholder="Scrivi qui le note di chiusura (facoltative)…" data-testid="close-notes-input"
            className="w-full mt-1 mb-2 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30" />
          <div className="text-[11px] text-muted-foreground mb-1">Facoltativo — spunta ciò che è stato completato:</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
            {[["problema_risolto", "Problema risolto"], ["condomino_informato", "Condòmino informato"], ["documenti_ricevuti", "Documenti ricevuti"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer" data-testid={`checklist-${k}`}>
                <input type="checkbox" className="accent-[#007BFF]" checked={!!checklist[k]} onChange={(e) => setChecklist((c) => ({ ...c, [k]: e.target.checked }))} />
                {lbl}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Input value={closeCost} onChange={(e) => setCloseCost(e.target.value)} placeholder="Costo effettivo € (facoltativo)" className="h-8 text-xs w-52" data-testid="close-cost-input" />
            <Button size="sm" className="h-8 text-xs bg-[#007BFF] hover:bg-[#0056B3]" data-testid="confirm-close" onClick={doClose}>Conferma chiusura</Button>
          </div>
        </div>
      )}

      {/* Pannello laterale (info chiave) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Info icon={Building2} label="Condominio" value={condo.building_name || "—"} sub={condo.building_id ? `Confidenza: ${condo.confidence}` : "Non identificato"} />
        <Info icon={MapPin} label="Luogo" value={p.place || "—"} />
        <Info icon={FileText} label="Categoria" value={catLabel(p.category)} sub={p.subcategory} />
        <Info icon={User} label="Segnalante" value={p.sender_name || p.sender_email || "—"}
          badge={linkedResident ? roleBadge(linkedResident.ruolo) : null}
          sub={linkedResident && linkedResident.interno ? `Interno ${linkedResident.interno}${linkedResident.scala ? ` · Scala ${linkedResident.scala}` : ""}` : undefined} />
        <Info icon={Wrench} label="Fornitore" value={(p.supplier || {}).name || "—"} sub={p.supplier_replied ? "✓ Risposta ricevuta" : (p.awaiting_supplier_reply ? "In attesa di risposta…" : undefined)} />
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Stato</div>
          {isClosed ? (
            <div className="h-8 flex items-center text-xs font-semibold" data-testid="pratica-status-static">{STATUS_LABELS[p.status] || p.status}</div>
          ) : (
            <Select value={p.status} onValueChange={changeStatus}>
              <SelectTrigger className="h-8 text-xs" data-testid="pratica-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {STATUS_ORDER.map((k) => <SelectItem key={k} value={k} className="text-xs">{STATUS_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Modifica manuale condominio/luogo/segnalante */}
      {editCondo && (
        <div className="bg-card border border-[#007BFF]/40 rounded-lg p-4 space-y-3" data-testid="pratica-edit-condo-panel">
          <h3 className="text-sm font-bold flex items-center gap-2"><Building2 className="h-4 w-4 text-[#007BFF]" />Modifica condominio, luogo e segnalante</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Condominio</label>
              <Select value={edBuilding || "__none__"} onValueChange={(v) => { const nb = v === "__none__" ? "" : v; setEdBuilding(nb); setEdResidentId(""); }}>
                <SelectTrigger className="h-9 text-sm mt-1" data-testid="edit-condo-select"><SelectValue placeholder="Seleziona condominio" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__" className="text-xs">Non identificato</SelectItem>
                  {buildings.map((b) => <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Luogo</label>
              <Input value={edPlace} onChange={(e) => setEdPlace(e.target.value)} placeholder="Es. Scala B, 3° piano, garage…" className="h-9 text-sm mt-1" data-testid="edit-place-input" />
            </div>
          </div>

          <div className="pt-2 border-t border-border/60">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><User className="h-3 w-3" />Segnalante</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
              <div>
                <span className="text-[10px] text-muted-foreground">Scegli dall'anagrafica</span>
                <Select value={edResidentId || "__manual__"} onValueChange={pickResident}>
                  <SelectTrigger className="h-9 text-sm mt-1" data-testid="edit-sender-resident-select"><SelectValue placeholder={edBuilding ? "Scegli un condòmino o inserisci a mano" : "Prima scegli il condominio"} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__manual__" className="text-xs">Inserimento manuale</SelectItem>
                    {edResidents.map((res) => <SelectItem key={res.id} value={res.id} className="text-xs">{res.nome}{res.interno ? ` · Int. ${res.interno}` : ""}{res.ruolo ? ` · ${res.ruolo === "proprietario" ? "Proprietario" : res.ruolo === "inquilino" ? "Inquilino" : res.ruolo}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Nome segnalante (campo libero)</span>
                <Input value={edSender} onChange={(e) => { setEdSender(e.target.value); setEdResidentId(""); }} placeholder="Nome del segnalante" className="h-9 text-sm mt-1" data-testid="edit-sender-name-input" />
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Email (facoltativa)</span>
                <Input value={edSenderEmail} onChange={(e) => setEdSenderEmail(e.target.value)} placeholder="email@esempio.it" className="h-9 text-sm mt-1" data-testid="edit-sender-email-input" />
              </div>
              <div>
                <span className="text-[10px] text-muted-foreground">Cellulare (facolt.)</span>
                <Input value={edSenderPhone} onChange={(e) => setEdSenderPhone(e.target.value)} placeholder="+39…" className="h-9 text-sm mt-1" data-testid="edit-sender-phone-input" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditCondo(false)}>Annulla</Button>
            <Button size="sm" className="h-8 text-xs bg-[#007BFF] hover:bg-[#0056B3]" data-testid="save-condo-btn" onClick={saveCondo} disabled={savingCondo}>
              {savingCondo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salva"}
            </Button>
          </div>
        </div>
      )}

      {/* Prossima azione & scadenze (Modulo 10/14) */}
      {!isClosed && p.runtime && (
        <Section icon={Clock} title="Prossima azione & scadenze" testid="pratica-runtime">
          <div className="flex items-start gap-2 mb-2">
            <span className="text-xs bg-[#007BFF]/10 text-[#007BFF] font-semibold px-2 py-1 rounded-md">→ {p.runtime.next_action}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className={`rounded-md p-2 border ${p.runtime.sla.first_response_overdue ? "border-orange-300 bg-orange-50" : "border-border"}`}>
              <div className="text-[10px] uppercase text-muted-foreground">Prima risposta</div>
              <div className="font-medium">{p.runtime.sla.first_response_met ? "✓ fatta" : (p.runtime.sla.first_response_overdue ? "in ritardo" : "entro " + (p.runtime.sla.first_response_due || "").replace("T", " ").slice(0, 16))}</div>
            </div>
            <div className={`rounded-md p-2 border ${p.runtime.sla.resolution_overdue ? "border-orange-300 bg-orange-50" : "border-border"}`}>
              <div className="text-[10px] uppercase text-muted-foreground">Risoluzione</div>
              <div className="font-medium">{p.runtime.sla.resolution_met ? "✓ risolta" : (p.runtime.sla.resolution_overdue ? "oltre tempo" : "entro " + (p.runtime.sla.resolution_due || "").replace("T", " ").slice(0, 16))}</div>
            </div>
          </div>
          {(p.runtime.attention_items || []).length > 0 && (
            <ul className="mt-2 space-y-1">
              {p.runtime.attention_items.map((it, i) => (
                <li key={i} className="text-xs flex items-center gap-1.5">
                  <AlertTriangle className={`h-3.5 w-3.5 ${it.severity === "alta" ? "text-orange-500" : "text-muted-foreground"}`} />
                  {it.label}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {/* Analisi AI */}
      <Section icon={Sparkles} title="Analisi AI · perché questa urgenza" testid="pratica-ai-analysis">
        <ul className="space-y-1">
          {((p.urgency || {}).reasons || []).map((r, i) => (
            <li key={i} className="text-xs flex items-start gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-[#007BFF] shrink-0 mt-0.5" />{r}</li>
          ))}
        </ul>
        {(p.danger_flags || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {p.danger_flags.map((d) => <span key={d} className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full">⚠ {d.replace(/_/g, " ")}</span>)}
          </div>
        )}
        {(p.missing_info || []).length > 0 && (
          <div className="mt-3 border-t border-border pt-2">
            <div className="text-xs font-semibold flex items-center gap-1.5 mb-1"><HelpCircle className="h-3.5 w-3.5" /> Informazioni mancanti</div>
            <ul className="space-y-0.5">
              {p.missing_info.map((m, i) => <li key={i} className="text-xs text-muted-foreground">• {m}</li>)}
            </ul>
          </div>
        )}
      </Section>

      {/* Duplicati */}
      {!isClosed && (p.duplicates || []).length > 0 && (
        <Section icon={Link2} title="Possibili duplicati / pratiche collegate" testid="pratica-duplicates">
          <p className="text-xs text-muted-foreground mb-2">L'AI propone. La decisione di unire è tua.</p>
          {p.duplicates.map((d) => (
            <div key={d.pratica_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border last:border-0">
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{d.ref} · {d.subject}</div>
                <div className="text-[11px] text-muted-foreground truncate">{d.reason} · affinità {d.score}%</div>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid={`link-dup-${d.pratica_id}`} onClick={() => linkPratica(d.pratica_id)}>Collega</Button>
            </div>
          ))}
        </Section>
      )}

      {/* Precedenti */}
      {(p.precedents || []).length > 0 && (
        <Section icon={History} title="Memoria operativa · precedenti" testid="pratica-precedents">
          {p.precedents.map((pr, i) => (
            <div key={i} className="text-xs py-1 flex items-start gap-1.5">
              <span className="text-[9px] uppercase mt-0.5 px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{pr.type === "pratica" ? "pratica" : "email"}</span>
              <span className="text-muted-foreground">{pr.label}</span>
            </div>
          ))}
        </Section>
      )}

      {/* Suggerimento fornitore */}
      {!isClosed && (
      <Section icon={Wrench} title="Fornitore consigliato" testid="pratica-supplier-suggestion">
        {sug.best ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium">{sug.best.name}</div>
                <div className="text-xs text-muted-foreground">{sug.best.reason}</div>
                {sug.best.email && <div className="text-[11px] text-muted-foreground">{sug.best.email}</div>}
              </div>
              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid="use-supplier"
                onClick={() => { setSupEmail(sug.best.email || ""); setSupName(sug.best.name || ""); setSupId(sug.best.id || ""); setSupPhone(sug.best.phone || ""); if (sug.best.phone && !sug.best.email && waEnabled) setInterChannel("sms"); toast.success("Fornitore selezionato per la richiesta"); }}>Usa questo</Button>
            </div>
            {(sug.alternatives || []).length > 0 && (
              <div className="mt-2 border-t border-border pt-2 space-y-1">
                {sug.alternatives.map((a) => (
                  <button key={a.id} onClick={() => { setSupEmail(a.email || ""); setSupName(a.name || ""); setSupId(a.id || ""); setSupPhone(a.phone || ""); }}
                    className="block w-full text-left text-xs text-muted-foreground hover:text-foreground">
                    · {a.name} <span className="opacity-60">{a.category}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-muted-foreground">{sug.note || "Nessun fornitore suggerito."}</p>
        )}
      </Section>
      )}

      {!isClosed && (<>
      {/* Bozza risposta al condòmino */}
      {!condo.building_id && !waHintDismissed && (
        <div className="bg-[#007BFF]/5 border border-[#007BFF]/30 rounded-lg p-3 flex items-start gap-2" data-testid="pratica-select-condo-hint">
          <Building2 className="h-4 w-4 text-[#007BFF] shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-foreground">
            Vuoi rispondere via SMS? Scegli qui il <strong>condominio</strong> di questa pratica (tra quelli che gestisci): se ha il piano <strong>Elite</strong> si attiveranno gli invii via SMS. È facoltativo.
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Select value={edBuilding || undefined} onValueChange={quickSetCondo} disabled={savingCondo}>
                <SelectTrigger className="h-8 text-xs w-64 bg-background" data-testid="pratica-quick-condo-select"><SelectValue placeholder="Seleziona un condominio…" /></SelectTrigger>
                <SelectContent>
                  {buildings.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nessun condominio in Gestione condomìni</div>}
                  {buildings.map((b) => <SelectItem key={b.id} value={b.id} className="text-xs">{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {savingCondo && <Loader2 className="h-4 w-4 animate-spin text-[#007BFF]" />}
              <Button size="sm" variant="ghost" className="h-8 text-[11px]" data-testid="pratica-select-condo-dismiss" onClick={() => setWaHintDismissed(true)}>Più tardi</Button>
            </div>
          </div>
        </div>
      )}

      <Section icon={Send} title="Bozza risposta / aggiornamento al condòmino" testid="pratica-condomino-draft">
        <div className="flex items-center gap-1 mb-2 bg-secondary rounded-md p-0.5 w-fit" data-testid="condomino-reply-channel">
          {replyChannels.map((k) => (
            <button key={k} type="button" data-testid={`condomino-channel-${k}`}
              disabled={k === "sms" && !waEnabled}
              title={k === "sms" && !waEnabled ? waDisabledReason : undefined}
              onClick={() => { if (k === "sms" && !waEnabled) return; setReplyChannel(k); }}
              className={`text-[11px] font-medium px-3 py-1 rounded inline-flex items-center gap-1 ${effReplyChannel === k ? (k === "sms" ? "bg-emerald-600 text-white" : "bg-[#007BFF] text-white") : "text-muted-foreground hover:text-foreground"} ${k === "sms" && !waEnabled ? "opacity-40 cursor-not-allowed" : ""}`}>
              {k === "sms" ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />}{k === "sms" ? "SMS" : "Email"}
            </button>
          ))}
        </div>
        {effReplyChannel === "sms" && !waSenderSet && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px] flex items-start gap-1.5" data-testid="sms-service-missing-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Il servizio SMS non è ancora attivo su questo account. Contatta l'assistenza Sedio.</span>
          </div>
        )}
        {effReplyChannel === "sms" && (
          <div className="mb-2">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><MessageCircle className="h-3 w-3" />Cellulare condòmino</label>
            <Input data-testid="condomino-wa-input" value={condoWa} onChange={(e) => setCondoWa(e.target.value)} placeholder="Cellulare condòmino (+39…)" className="h-8 text-xs" />
          </div>
        )}
        {effReplyChannel === "email" && !contactEmail && cc.found && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px]" data-testid="condomino-no-email-warning">
            <div className="flex items-start gap-1.5 mb-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>Il condòmino{condominoName ? ` "${condominoName}"` : ""} è nei contatti ma non ha un'email. Aggiungila qui:</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Input value={contactInput} onChange={(e) => setContactInput(e.target.value)} placeholder="email@esempio.it" className="h-7 text-[11px] flex-1" data-testid="inline-contact-email" />
              <Button size="sm" className="h-7 text-[11px] bg-[#007BFF] hover:bg-[#0056B3]" disabled={savingContact} data-testid="inline-contact-save" onClick={() => addContactInline("fill", cc.resident_id)}>Salva contatto</Button>
            </div>
          </div>
        )}
        {effReplyChannel === "email" && !contactEmail && !cc.found && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px]" data-testid="condomino-not-in-contacts-warning">
            <div className="flex items-start gap-1.5 mb-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span>{emailTo ? <>L'indirizzo <b>{emailTo}</b> non risulta tra i contatti.</> : "Questo mittente non è nei contatti condòmini."} Aggiungilo all'anagrafica {condo.building_name ? <>di <b>{condo.building_name}</b></> : ""}:</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Input value={contactInput} onChange={(e) => setContactInput(e.target.value)} placeholder={emailTo || "email@esempio.it"} className="h-7 text-[11px] flex-1" data-testid="inline-contact-email" />
              <Button size="sm" className="h-7 text-[11px] bg-[#007BFF] hover:bg-[#0056B3]" disabled={savingContact} data-testid="inline-contact-save"
                onClick={() => addContactInline("create", condo.building_id, condominoName)}>Aggiungi contatto</Button>
            </div>
            {!condo.building_id && <div className="mt-1 text-[10px] text-amber-700">Assegna prima un condominio alla pratica per salvare il contatto.</div>}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {[["ricevuta", "Presa in carico"], ["programmato", "Intervento programmato"], ["risolta", "Risolta"]].map(([k, lbl]) => (
            <button key={k} type="button" data-testid={`condomino-template-${k}`} onClick={() => setReply(condominoTemplate(k, p, effReplyChannel))}
              className="text-[11px] border border-border rounded-full px-2.5 py-1 hover:border-[#007BFF] hover:text-[#007BFF] transition-colors">{lbl}</button>
          ))}
        </div>
        <Textarea data-testid="condomino-reply-text" value={effReplyChannel === "sms" ? reply.slice(0, 160) : reply} maxLength={effReplyChannel === "sms" ? 160 : undefined} onChange={(e) => setReply(effReplyChannel === "sms" ? e.target.value.slice(0, 160) : e.target.value)} rows={effReplyChannel === "sms" ? 6 : 10} className={`text-sm ${effReplyChannel === "sms" ? "min-h-[150px]" : "min-h-[220px]"}`} placeholder="Bozza generata dall'AI…" />
        {effReplyChannel === "sms" && (
          <div className="mt-1 flex items-center justify-end gap-1 text-[11px]" data-testid="sms-char-counter">
            <span className={reply.slice(0, 160).length >= 160 ? "text-red-600 font-semibold" : "text-muted-foreground"}>{reply.slice(0, 160).length}/160 caratteri · 1 SMS</span>
          </div>
        )}
        {effReplyChannel === "email" && (
          <AttachBar items={condoAtts} onAdd={(e) => uploadAttachment(e, "condo")} onRemove={(fid) => removeAtt("condo", fid)} onOpen={openAttachment} uploading={uploadingAtt === "condo"} testid="condomino-attach" />
        )}
        <div className="flex items-center justify-between gap-2 mt-2">
          {effReplyChannel === "sms" ? (
            <span className="text-[11px] text-emerald-700 font-medium truncate flex items-center gap-1"><MessageCircle className="h-3 w-3" />SMS: {condoWa ? `+${condoWa.replace(/^\+/, "")}` : "— (inserisci il numero)"}</span>
          ) : (
            <span className="text-[11px] text-muted-foreground truncate flex items-center gap-1"><Mail className="h-3 w-3" />A: {emailTo || "— (nessuna email nei contatti)"}{contactEmail && <span className="text-[#007BFF]"> · dai contatti</span>}</span>
          )}
          <Button size="sm" className={`h-8 text-xs ${effReplyChannel === "sms" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#007BFF] hover:bg-[#0056B3]"}`} data-testid="approve-condomino-reply"
            disabled={busy === "send_condomino_reply" || !replyTo || (effReplyChannel === "sms" && !waSenderSet)}
            onClick={() => approve("send_condomino_reply", {
              to: replyTo,
              channel: effReplyChannel,
              attachment_ids: effReplyChannel === "email" ? condoAtts.map((a) => a.file_id) : [],
              body: effReplyChannel === "sms" ? capSms160(reply) : reply, subject: `Aggiornamento: ${p.subject}` })}>
            {busy === "send_condomino_reply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (effReplyChannel === "sms" ? "Approva e invia via SMS" : "Approva e invia via email")}
          </Button>
        </div>
      </Section>

      {/* Bozza richiesta intervento */}
      <Section icon={Wrench} title="Bozza richiesta di intervento" testid="pratica-intervention-draft">
        <div className="mb-2">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1"><Wrench className="h-3 w-3" />Cambia fornitore</label>
          <Select value={supId || "__manual__"} onValueChange={pickSupplier}>
            <SelectTrigger className="h-8 text-xs" data-testid="change-supplier-select"><SelectValue placeholder="Scegli un fornitore dalla rubrica" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__manual__" className="text-xs">Inserimento manuale</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}{s.category ? ` · ${s.category}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1 mb-2 bg-secondary rounded-md p-0.5 w-fit">
          {[["email", "Email"], ["sms", "SMS"]].map(([k, lbl]) => (
            <button key={k} type="button" data-testid={`intervention-channel-${k}`}
              disabled={k === "sms" && !waEnabled}
              title={k === "sms" && !waEnabled ? waDisabledReason : undefined}
              onClick={() => { if (k === "sms" && !waEnabled) return; setInterChannel(k); }}
              className={`text-[11px] font-medium px-3 py-1 rounded ${interChannel === k ? (k === "sms" ? "bg-emerald-600 text-white" : "bg-[#007BFF] text-white") : "text-muted-foreground hover:text-foreground"} ${k === "sms" && !waEnabled ? "opacity-40 cursor-not-allowed" : ""}`}>{lbl}</button>
          ))}
        </div>
        {interChannel === "sms" && waEnabled && !waSenderSet && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px] flex items-start gap-1.5" data-testid="sms-service-missing-warning-supplier">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Il servizio SMS non è ancora attivo su questo account. Contatta l'assistenza Sedio.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Input data-testid="supplier-name-input" value={supName} onChange={(e) => setSupName(e.target.value)} placeholder="Nome fornitore" className="h-8 text-xs" />
          {interChannel === "sms" ? (
            <Input data-testid="supplier-phone-input" value={supPhone} onChange={(e) => setSupPhone(e.target.value)} placeholder="Cellulare fornitore (+39…)" className="h-8 text-xs" />
          ) : (
            <Input data-testid="supplier-email-input" value={supEmail} onChange={(e) => setSupEmail(e.target.value)} placeholder="Email fornitore" className="h-8 text-xs" />
          )}
        </div>
        {interChannel === "email" && supName && !supEmail && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px] flex items-start gap-1.5" data-testid="supplier-no-email-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Il fornitore selezionato non ha un'email nei contatti. <Link to="/fornitori" className="font-semibold underline hover:text-amber-900">Aggiungi/modifica fornitore →</Link> oppure passa a SMS.</span>
          </div>
        )}
        {interChannel === "sms" && supName && !supPhone && (
          <div className="mb-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[11px] flex items-start gap-1.5" data-testid="supplier-no-phone-warning">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>Il fornitore selezionato non ha un numero di cellulare nei contatti. <Link to="/fornitori" className="font-semibold underline hover:text-amber-900">Aggiungi/modifica fornitore →</Link> oppure usa l'email.</span>
          </div>
        )}
        <Textarea data-testid="intervention-text" value={interv} onChange={(e) => setInterv(e.target.value)} rows={6} className="text-sm" placeholder="Bozza richiesta intervento…" />
        {interChannel === "email" && (
          <AttachBar items={interAtts} onAdd={(e) => uploadAttachment(e, "inter")} onRemove={(fid) => removeAtt("inter", fid)} onOpen={openAttachment} uploading={uploadingAtt === "inter"} testid="intervention-attach" />
        )}
        <div className="flex items-center justify-end gap-2 mt-2">
          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="approve-supplier-reminder"
            disabled={busy === "send_supplier_reminder" || (interChannel === "sms" ? (!supPhone || !waSenderSet) : !supEmail)}
            onClick={() => approve("send_supplier_reminder", { to: interChannel === "sms" ? supPhone : supEmail, channel: interChannel, supplier_phone: supPhone })}>
            {busy === "send_supplier_reminder" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sollecita"}
          </Button>
          <Button size="sm" className={`h-8 text-xs ${interChannel === "sms" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#007BFF] hover:bg-[#0056B3]"}`} data-testid="approve-intervention-request"
            disabled={busy === "send_intervention_request" || (interChannel === "sms" ? (!supPhone || !waSenderSet) : !supEmail)}
            onClick={() => approve("send_intervention_request", { to: interChannel === "sms" ? supPhone : supEmail, channel: interChannel, supplier_id: supId, supplier_name: supName, supplier_phone: supPhone, attachment_ids: interChannel === "email" ? interAtts.map((a) => a.file_id) : [], body: interv, subject: `Richiesta intervento — ${p.ref}: ${p.subject}` })}>
            {busy === "send_intervention_request" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (interChannel === "sms" ? "Invia via SMS" : "Approva e invia al fornitore")}
          </Button>
        </div>
      </Section>
      </>)}

      {/* Timeline */}
      <Section icon={Clock} title="Cronologia pratica" testid="pratica-timeline">
        <ol className="relative border-l border-border ml-1.5 space-y-3">
          {(p.history || []).slice().reverse().map((h, i) => (
            <li key={i} className="ml-4">
              <span className="absolute -left-[5px] h-2.5 w-2.5 rounded-full bg-[#007BFF]" />
              <div className="text-xs flex items-start gap-1.5">
                <span>{h.label}</span>
                {h.public && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0" data-testid={`timeline-public-${i}`}>Al condòmino</span>}
              </div>
              {h.text && (
                <div className="mt-1 text-[11px] text-foreground/80 bg-secondary/60 border border-border rounded-md px-2 py-1.5 whitespace-pre-wrap break-words" data-testid={`timeline-text-${i}`}>
                  {h.text}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground">{(h.ts || "").replace("T", " ").slice(0, 16)}</div>
            </li>
          ))}
        </ol>
        {!isClosed && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input data-testid="pratica-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={notePublic ? "Scrivi un aggiornamento per il condòmino…" : "Aggiungi una nota interna…"} className="h-8 text-xs" onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="pratica-add-note" onClick={addNote}>Aggiungi</Button>
            </div>
            <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="pratica-note-public-label">
              <input type="checkbox" data-testid="pratica-note-public-toggle" checked={notePublic} onChange={(e) => setNotePublic(e.target.checked)} className="h-3.5 w-3.5 rounded border-border accent-[#007BFF]" />
              Visibile al condòmino (compare come aggiornamento nel suo portale)
            </label>
          </div>
        )}
      </Section>
    </div>
  );
};

const AttachBar = ({ items, onAdd, onRemove, onOpen, uploading, testid }) => (
  <div className="mt-2" data-testid={testid}>
    <label className="inline-flex items-center gap-1.5 text-xs border border-dashed border-border rounded-md px-3 py-1.5 cursor-pointer hover:border-[#007BFF] transition-colors" data-testid={`${testid}-label`}>
      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
      {uploading ? "Caricamento…" : "Allega documento / foto (PDF, DOCX, XLSX, immagini)"}
      <input type="file" className="hidden" accept=".pdf,.docx,.xlsx,.txt,.png,.jpg,.jpeg,.webp" onChange={onAdd} disabled={!!uploading} data-testid={`${testid}-input`} />
    </label>
    {items.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((a) => (
          <div key={a.file_id} className="flex items-center gap-2 text-xs bg-secondary rounded-md p-1.5 pr-2 border border-border" data-testid={`${testid}-item`}>
            <button type="button" onClick={() => (a.preview ? window.open(a.preview, "_blank") : onOpen(a.file_id))}
              className="flex items-center gap-2 hover:text-[#007BFF]" title="Apri anteprima" data-testid={`${testid}-open`}>
              {a.preview ? (
                <img src={a.preview} alt={a.filename} className="h-9 w-9 rounded object-cover border border-border" />
              ) : (
                <span className="h-9 w-9 rounded bg-[#007BFF]/10 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-[#007BFF]" /></span>
              )}
              <span className="truncate max-w-[140px]">{a.filename}</span>
            </button>
            <button type="button" onClick={() => onRemove(a.file_id)} className="p-0.5 hover:text-red-600" data-testid={`${testid}-remove`}><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Info = ({ icon: Icon, label, value, sub, badge }) => (
  <div className="bg-card border border-border rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="text-sm font-medium truncate" title={value}>{value}</div>
      {badge}
    </div>
    {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
  </div>
);

// Taglia un testo entro 160 caratteri per l'SMS, preferendo un confine di frase/parola.
function capSms160(text, maxlen = 160) {
  let t = String(text || "").replace(/[""]/g, '"').replace(/['']/g, "'").replace(/[–—]/g, "-").replace(/…/g, "...");
  t = t.replace(/[ \t]+/g, " ").replace(/\n/g, " ").trim();
  if (t.length <= maxlen) return t;
  const cut = t.slice(0, maxlen);
  const m = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  if (m >= 80) return cut.slice(0, m + 1).trim();
  const sp = cut.lastIndexOf(" ");
  if (sp >= 80) return cut.slice(0, sp).trim();
  return cut.trim();
}

function condominoTemplate(kind, p, channel) {
  const raw = (p.sender_name || "").trim();
  // Non usare l'email come nome: se manca o è un'email, saluto generico.
  const nome = (raw && !raw.includes("@")) ? raw : "Condòmino";
  const ogg = p.subject || "la sua segnalazione";
  if (channel === "sms") {
    // Varianti brevi per SMS: sempre entro 160 caratteri, senza emoji e SENZA email/oggetto.
    const s = {
      ricevuta: `Gentile ${nome}, abbiamo preso in carico la sua segnalazione e aperto la pratica. La aggiorneremo. L'Amministrazione`,
      programmato: `Gentile ${nome}, a seguito della sua segnalazione e' stato programmato un intervento tecnico. Le confermeremo la data. L'Amministrazione`,
      risolta: `Gentile ${nome}, la problematica segnalata risulta risolta. Restiamo a disposizione. L'Amministrazione`,
    };
    return (s[kind] || s.ricevuta).slice(0, 160);
  }
  const t = {
    ricevuta: `Gentile ${nome},\n\nconfermiamo di aver ricevuto e preso in carico la sua segnalazione relativa a "${ogg}". Abbiamo aperto la relativa pratica e la aggiorneremo sugli sviluppi.\n\nCordiali saluti.`,
    programmato: `Gentile ${nome},\n\nla informiamo che, a seguito della sua segnalazione ("${ogg}"), è stato programmato un intervento tecnico. La terremo aggiornata non appena avremo la conferma della data.\n\nCordiali saluti.`,
    risolta: `Gentile ${nome},\n\nla informiamo che la problematica segnalata ("${ogg}") risulta risolta. Restiamo a disposizione per qualsiasi ulteriore necessità.\n\nCordiali saluti.`,
  };
  return t[kind] || t.ricevuta;
}

